<template>
    <div class="payment-breakdown__root">
        <button
            v-if="isMobile"
            class="payment-breakdown__toggle"
            @click="isBreakdownExpanded = !isBreakdownExpanded"
        >
            {{
                isBreakdownExpanded
                    ? t("organisationHeader.hideDetailsCTA")
                    : t("organisationHeader.viewDetailsCTA")
            }}
            <svg
                class="payment-breakdown__toggle-icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
            >
                <path
                    :d="isBreakdownExpanded ? mdiChevronUp : mdiChevronDown"
                    fill="currentColor"
                />
            </svg>
        </button>

        <Transition name="expand">
            <div
                v-if="!isMobile || isBreakdownExpanded"
                class="payment-breakdown"
            >
                <div class="payment-breakdown__section-header">
                    {{ t("organisationHeader.paymentDetailsLabel") }}
                </div>
                <div v-if="emailAddress" class="payment-breakdown__row">
                    <span class="payment-breakdown__label">{{
                        t("organisationHeader.emailForReceiptsLabel")
                    }}</span>
                    <span class="payment-breakdown__value">{{
                        emailAddress
                    }}</span>
                </div>
                <template v-if="supplementaryBreakdown.length > 0">
                    <div class="payment-breakdown__section-header">
                        {{ t("organisationHeader.invoiceDetailsLabel") }}
                    </div>
                    <div
                        v-for="item in supplementaryBreakdown"
                        :key="item.label"
                        class="payment-breakdown__row"
                    >
                        <span class="payment-breakdown__label">{{
                            t(item.label)
                        }}</span>
                        <span class="payment-breakdown__value">{{
                            item.value
                        }}</span>
                    </div>
                </template>
                <div
                    v-if="!isCardPaymentActionInProgress"
                    class="payment-breakdown__edit"
                >
                    <span>{{
                        t("organisationHeader.detailsIncorrectLabel")
                    }}</span>
                    <button
                        class="payment-breakdown__edit-btn ml-1"
                        @click="onEditClick"
                    >
                        {{ t("organisationHeader.editCTA") }}
                    </button>
                </div>
            </div>
        </Transition>

        <partner-link-edit-dialog
            v-model="showPartnerModal"
            :organisation-name="
                organisationStore.organisation?.organisationName ?? ''
            "
        />
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useMediaQuery } from "@vueuse/core";
import { useRouter } from "vue-router";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import { useOrganisationStore } from "@/plugins/store";
import { isPartnerLink } from "@/helpers/organisation";
import { routeNames } from "@/constants/routeNames";
import PartnerLinkEditDialog from "@/components/layout/partnerLinkEditDialog.vue";
import { useSubmitPayment } from "@/composables/useSubmitPayment";

defineProps<{
    emailAddress: string | null | undefined;
    supplementaryBreakdown: Array<{ label: string; value: string }>;
}>();

const { t } = useI18n();
const organisationStore = useOrganisationStore();
const router = useRouter();
const { isCardPaymentActionInProgress } = useSubmitPayment();

const isMobile = useMediaQuery("(max-width: 839px)");
const isBreakdownExpanded = ref(false);
const showPartnerModal = ref(false);

/**
 * Navigates the user to the first step of the payment journey, which is either
 * the invoice or form page depending on the organisation's configuration.
 * If the payment was initiated by a partner, it shows a modal instead of navigating.
 */
const onEditClick = () => {
    if (isPartnerLink()) {
        showPartnerModal.value = true;
        return;
    }

    const shortcode = organisationStore.shortcode;
    const formName = organisationStore.formName;
    if (formName === "invoice" || formName === null) {
        router.push({ name: routeNames.INVOICE, params: { shortcode } });
    } else {
        router.push({
            name: routeNames.FORM,
            params: { shortcode, form: formName },
        });
    }
};
</script>

<style scoped>
.payment-breakdown__root {
    align-self: stretch;
}

.payment-breakdown__toggle {
    width: calc(100% + 32px);
    margin: 4px -16px -18px;
    padding: 10px 16px;
    background: none;
    font-size: 13px;
    font-weight: 500;
    color: var(--action-primary);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.payment-breakdown__toggle-icon {
    flex-shrink: 0;
    transition: transform 0.3s ease;
}

.payment-breakdown__toggle-icon--rotated {
    transform: rotate(180deg);
}

.payment-breakdown {
    align-self: stretch;
    margin: 4px -16px -16px;
    padding: 12px 16px 16px;
    background-color: var(--white);
    display: flex;
    flex-direction: column;
    gap: 6px;
}

@media (max-width: 839px) {
    .payment-breakdown {
        margin: 14px -16px -16px;
        border-top: 1px solid var(--surface-disabled);
    }
}

.payment-breakdown__section-header {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 6px;
}

.payment-breakdown__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
}

.payment-breakdown__label {
    color: var(--text-secondary);
    flex-shrink: 0;
}

.payment-breakdown__value {
    color: var(--text-primary);
    font-weight: 500;
    text-align: right;
    word-break: break-word;
}

.payment-breakdown__edit {
    margin: 8px -16px -16px;
    padding: 10px 16px 12px;
    border-top: 1px solid var(--surface-disabled);
    font-size: 13px;
    color: var(--text-secondary);
    text-align: left;
}

.payment-breakdown__edit-btn {
    background: none;
    border: none;
    padding: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--action-primary);
    cursor: pointer;
    text-decoration: underline;
}

.expand-enter-active,
.expand-leave-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
